import { Viewer, ProgressBar, DocumentLoadEvent } from '@react-pdf-viewer/core';
import {
  RenderCurrentPageLabelProps,
  pageNavigationPlugin,
} from '@react-pdf-viewer/page-navigation';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { AppButton } from '../../../general/AppButton/AppButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import { AppDropDown2 } from '../../../general/AppDropDown2/AppDropDown2';
import { AppCheckBox } from '../../../general/AppCheckBox/AppCheckBox';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import moment from 'moment';
import AppDatePicker from '../../../general/AppDatePicker/AppDatePicker';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  FiledLienFieldData,
  FiledLienTemplate,
  FiledLienTemplateField,
  PDFFieldFiledLienTemplate,
  FromFieldTypeFiledLienTemplate,
  SupplierJobApiResponse,
  formatMoney,
} from '@dill/dill-shared';
import {
  removePDFFields,
  populatePagesPdfWithFieldsData,
  structureTemplateNewFieldFromTypePDFData,
  structureTemplateNonNewFieldFromTypePDFData,
  downloadFile,
  flattenPDF,
  buildSupplierPath,
  stripSupplierPrefix,
} from '../../../../utils/helpers';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS } from '../../../../utils/colors';
import successChecksCircle from '../../../../assets/svg/success_checks_circle.svg';
import { useTrivialValueDebounce } from '../../../../utils/hooks/useDebounce';
import { getFiledLienTemplates } from '../../../../redux/services/filedLienTemplatesService';
import { getSupplierJob, getSupplierJobs } from '../../../../redux/services/supplierJobsService';
import { createFiledLien } from '../../../../redux/services/filedLiensService';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { CaretCircleLeft, CaretCircleRight, Note } from 'phosphor-react';
import { AppSuccessModal } from '../../../general/AppSuccessModal/AppSuccessModal';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import {
  createUserSignature,
  deleteUserSignature,
  getUserDetails,
  getUserSignatures,
  updateUserDetails,
} from '../../../../redux/services/authService';
import { AppNewSupplierJobModal } from '../../../general/AppNewSupplierJobModal/AppNewSupplierJobModal';
import { getRequestedSupplierJobs } from '../../../../redux/services/requestJobInformationService';
import AppRequestedJobsModal from '../../../general/AppRequestedJobsModal/AppRequestedJobsModal';
import { AppImagePreviewModal } from '../../../general/AppImagePreviewModal/AppImagePreviewModal';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import emptyListImg from '../../../../assets/images/emptyList.png';
import { AppInvoiceTable } from '../../../general/AppInvoiceTable/AppInvoiceTable';

const AddFiledLien = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { state } = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, userSignatures } = useAppSelector((globalState) => globalState.auth);
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  // Tracks the active supplier id for in-flight job fetches, so a chain started for a
  // previous supplier stops once the supplier is switched (otherwise its results would be
  // written into the now differently-scoped store). The request ref lets us abort in-flight.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightJobsRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightJobsRequestRef.current?.abort(), [currentSupplier?.id]);
  const { filedLienTemplates } = useAppSelector((globalState) => globalState.filedLienTemplates);
  const { supplierJobsNoInvoicesMap } = useAppSelector((globalState) => globalState.supplierJobs);
  const { requestedSupplierJobs } = useAppSelector(
    (globalState) => globalState.requestJobInformation
  );

  const [selectedTemplate, setSelectedTemplate] = useState<FiledLienTemplate | null>(null);
  const [selectedTemplateFields, setSelectedTemplateFields] = useState<
    FiledLienTemplateField[] | null
  >(null);
  const [selectedJob, setSelectedJob] = useState<SupplierJobApiResponse | null>(null);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const zoomPluginInstance = zoomPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
  const { CurrentPageLabel, jumpToNextPage, jumpToPreviousPage } = pageNavigationPluginInstance;

  const [currentPdfUrl, setCurrentPdfUrl] = useState('');
  const [flattenedPdfUrl, setFlattenedPdfUrl] = useState('');
  const [debouncedPopulatePdfChange, triggerDebouncedPopulatePdf] = useTrivialValueDebounce(200);
  const [showErrorAbove, setShowErrorAbove] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formFieldsData, setFormFieldsData] = useState<{
    [x: string]: FiledLienFieldData;
  }>({});
  const [isFiledLienCreateSuccessModalOpen, setIsFiledLienCreateSuccessModalOpen] = useState(false);
  const [isCreateSignatureModalOpen, setIsCreateSignatureModalOpen] = useState(false);
  const [signature, setSignature] = useState('');
  const [manualSign, setManualSign] = useState(false);
  const [defaultIsManualSign, setDefaultIsManualSign] = useState(false);
  const [missingJobDetails, setMissingJobDetails] = useState(false);
  const [openEditJob, setOpenEditJob] = useState(false);
  const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false);
  const [missingJobDetailsList, setMissingJobDetailsList] = useState<string[]>([]);
  const [openRequestedJobsModal, setOpenRequestedJobsModal] = useState<boolean>(false);
  const [showPreviewPdf, setShowPreviewPdf] = useState(false);
  const [selectedAttachmentUrl, setSelectedAttachmentUrl] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [showPreviewImage, setShowPreviewImage] = useState(false);
  const [editSupplierJob, setEditSupplierJob] = useState<SupplierJobApiResponse | null>(null);

  const templates = useMemo(() => {
    if (filedLienTemplates.length > 0) {
      const processedFiledLienTemplates = filedLienTemplates.filter(
        (template) => template.status === 'processed'
      );
      return processedFiledLienTemplates;
    }
    return [];
  }, [filedLienTemplates]);

  const filteredTemplates = useMemo(() => {
    let templatesFiltered: FiledLienTemplate[] = [];
    if (templates.length > 0 && selectedJob) {
      templatesFiltered = templates.filter((template) => {
        if (
          template?.state &&
          selectedJob?.jobState &&
          template.state.toUpperCase() === selectedJob.jobState.toUpperCase()
        ) {
          return true;
        }
        return false;
      });
    }
    if (templatesFiltered.length > 0 && location.pathname.split('/').length !== 5) {
      const template = templatesFiltered[0];
      setSelectedTemplate(template);
      if (template.originalPdf?.url) {
        setCurrentPdfUrl(template.originalPdf?.url);
      }
    } else {
      setSelectedTemplate(null);
      setCurrentPdfUrl('');
    }
    return templatesFiltered;
  }, [templates, selectedJob]);

  const recursiveFetchSupplierJob = async ({
    lastDockId,
    fetchJobs,
    supplierId,
  }: {
    lastDockId: string;
    fetchJobs: SupplierJobApiResponse[];
    supplierId: string;
  }) => {
    // A newer fetch (e.g. after switching suppliers) supersedes this chain — stop so we
    // don't write the previous supplier's jobs into the store.
    if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
      return;
    }
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    setDataFullyLoaded(false);

    const request = dispatch(
      getSupplierJobs({
        supplierId,
        includeInvoices: false,
        includePreLiens: false,
        previousLastDocumentId: currentPreviousLastDocumentId,
        pageSize,
        sortOrder: 'asc',
        sortBy: 'name',
        isHideLoadingIndicator: true,
        isOnlyArchivedJobs: false,
      })
    );
    inFlightJobsRequestRef.current = request;
    const results = await request;
    if (activeFetchSupplierIdRef.current !== supplierId) {
      return;
    }
    if (results.type === 'supplierJobs/getSupplierJobs/fulfilled') {
      if (results.payload) {
        const payload = results.payload as any;
        const fetchedSupplierJobs: SupplierJobApiResponse[] = payload.data as any;
        const lastDocumentId = payload.lastDocumentId;
        if (
          lastDocumentId === currentPreviousLastDocumentId ||
          fetchedSupplierJobs.length === pageSize
        ) {
          currentPreviousLastDocumentId = lastDocumentId;
          await recursiveFetchSupplierJob({
            lastDockId: lastDocumentId,
            fetchJobs: [...fetchJobs, ...fetchedSupplierJobs],
            supplierId,
          });
        } else {
          console.log('No more pages to fetch');
          setDataFullyLoaded(true);
        }
      }
    } else {
      console.log('Fetch failed or interrupted');
      setDataFullyLoaded(true);
    }
  };

  const fetchData = async () => {
    const fetchSupplierId = currentSupplier?.id || '';
    await dispatch(getFiledLienTemplates({ supplierId: fetchSupplierId }));
    if (fetchSupplierId) {
      recursiveFetchSupplierJob({ lastDockId: '', fetchJobs: [], supplierId: fetchSupplierId });
    }
    await dispatch(getUserSignatures());
  };

  useEffect(() => {
    if (user) {
      dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
      fetchData();
    }

    return () => {};
  }, [user?.id]);

  const checkMissingJobDetails = (job: SupplierJobApiResponse | null) => {
    if (job) {
      const detailsMissingList: string[] = [];
      if (!job.isOwnerUnknown && job?.owners?.filter((own) => own.name !== '')?.length === 0) {
        detailsMissingList.push('Owner');
      }
      if (!job.isGCUnknown && job?.GCs?.filter((gc) => gc.name !== '')?.length === 0) {
        detailsMissingList.push('GC');
      }
      if (!job?.estimatedMaterialAmount) {
        detailsMissingList.push('Estimated Material Amount');
      }
      setMissingJobDetailsList(detailsMissingList);
      if (
        (!job.isOwnerUnknown && job?.owners?.filter((own) => own.name !== '')?.length === 0) ||
        (!job.isGCUnknown && job?.GCs?.filter((gc) => gc.name !== '')?.length === 0) ||
        !job?.estimatedMaterialAmount
      ) {
        return true;
      }
    }
    return false;
  };

  const populatePdf = async (fieldDetails: PDFFieldFiledLienTemplate[]) => {
    if (!selectedTemplate?.originalPdf?.url) {
      return;
    }

    let pdfUrl = '';
    if (flattenedPdfUrl) {
      pdfUrl = flattenedPdfUrl;
    } else {
      const flatPDF = await removePDFFields(selectedTemplate?.originalPdf?.url ?? '');
      pdfUrl = flatPDF ?? '';
    }
    const populatedPdfUrl = await populatePagesPdfWithFieldsData({
      fieldDetails,
      url: pdfUrl,
      releaseId: `${user?.id ?? ''}__${new Date().getTime()}`,
    });
    if (populatedPdfUrl) {
      setCurrentPdfUrl(populatedPdfUrl);
    }
  };

  const getVendorDetails = () => {
    return {};
  };

  const populateTemplateFieldData = () => {
    let data: { [x: string]: FiledLienFieldData } = {};
    if (selectedTemplate) {
      selectedTemplate.formFields.forEach((formField) => {
        if (formField.fromFieldType === 'NEW_FIELD' && formField.whoPopulatesField === 'SUPPLIER') {
          let defaultValue: any = '';
          if (
            formField.fieldType === 'CURRENCY' &&
            formField.defaultValue !== null &&
            formField.defaultValue !== undefined
          ) {
            defaultValue = Number(formField.defaultValue).toFixed(2);
          } else if (formField.defaultValue !== null && formField.defaultValue !== undefined) {
            defaultValue = `${formField.defaultValue}`;
          } else {
            defaultValue = formField.defaultValue;
          }
          data = {
            ...data,
            [formField.id]: {
              isRequired: formField.isFieldRequired,
              value:
                formFieldsData[formField.id] && formFieldsData[formField.id].value
                  ? formFieldsData[formField.id].value
                  : formField.defaultValue !== null && formField.defaultValue !== undefined
                  ? defaultValue
                  : '',
              id: formField.id,
              name: formField.fieldName,
              type: formField.fieldType,
              ...(formFieldsData[formField.id]?.fontSize
                ? { fontSize: formFieldsData[formField.id].fontSize }
                : {}),
            },
          };
        }
      });
    }
    return { ...data };
  };

  const populateAllPdfData = () => {
    const specialFieldsPDFData: {
      fromFieldType: FromFieldTypeFiledLienTemplate;
      dataToUse: any;
    }[] = [];

    const dynamicForFieldsPdfData: {
      fromFieldId: string;
      value: any;
    }[] = [];

    if (selectedJob) {
      const filteredSelectedJob = {
        ...selectedJob,
        GC: selectedJob.GCs?.[0]?.name ?? '',
        GCAddress: selectedJob.GCs?.[0]?.address ?? '',
        GCCity: selectedJob.GCs?.[0]?.city ?? '',
        GCState: selectedJob.GCs?.[0]?.state ?? '',
        GCZipCode: selectedJob.GCs?.[0]?.zipCode ?? '',
        owner: selectedJob.owners?.[0]?.name ?? '',
        ownerAddress: selectedJob.owners?.[0]?.address ?? '',
        ownerCity: selectedJob.owners?.[0]?.city ?? '',
        ownerState: selectedJob.owners?.[0]?.state ?? '',
        ownerZipCode: selectedJob.owners?.[0]?.zipCode ?? '',
        surety: selectedJob.sureties?.[0]?.name ?? '',
        suretyAddress: selectedJob.sureties?.[0]?.address ?? '',
        suretyCity: selectedJob.sureties?.[0]?.city ?? '',
        suretyState: selectedJob.sureties?.[0]?.state ?? '',
        suretyZipCode: selectedJob.sureties?.[0]?.zipCode ?? '',
        bondNumber: selectedJob.sureties?.[0]?.bondNumber ?? '',
      };
      const allNewFormFields = { ...formFieldsData };
      Object.keys(allNewFormFields).forEach((formFieldKey) => {
        if (
          allNewFormFields[formFieldKey].value !== 'X' &&
          allNewFormFields[formFieldKey].id === 'checkContractor' &&
          filteredSelectedJob.GCZipCode
        ) {
          filteredSelectedJob.GC = '';
          filteredSelectedJob.GCAddress = '';
          filteredSelectedJob.GCCity = '';
          filteredSelectedJob.GCState = '';
          filteredSelectedJob.GCZipCode = '';
        }
        if (
          allNewFormFields[formFieldKey].value !== 'X' &&
          allNewFormFields[formFieldKey].id === 'checkLender' &&
          filteredSelectedJob.lenderZipCode
        ) {
          delete filteredSelectedJob.lender;
          delete filteredSelectedJob.lenderAddress;
          delete filteredSelectedJob.lenderCity;
          delete filteredSelectedJob.lenderState;
          delete filteredSelectedJob.lenderZipCode;
        } else {
          filteredSelectedJob.lender = selectedJob.lenders?.[0]?.name ?? '';
          filteredSelectedJob.lenderAddress = selectedJob.lenders?.[0]?.address ?? '';
          filteredSelectedJob.lenderCity = selectedJob.lenders?.[0]?.city ?? '';
          filteredSelectedJob.lenderState = selectedJob.lenders?.[0]?.state ?? '';
          filteredSelectedJob.lenderZipCode = selectedJob.lenders?.[0]?.zipCode ?? '';
        }
        if (
          allNewFormFields[formFieldKey].value !== 'X' &&
          allNewFormFields[formFieldKey].id === 'checkOwner'
        ) {
          filteredSelectedJob.owner = '';
          filteredSelectedJob.ownerAddress = '';
          filteredSelectedJob.ownerCity = '';
          filteredSelectedJob.ownerState = '';
          filteredSelectedJob.ownerZipCode = '';
        }
      });
      if (filteredSelectedJob?.estimatedMaterialAmount) {
        filteredSelectedJob.estimatedMaterialAmount = formatMoney(
          filteredSelectedJob.estimatedMaterialAmount,
          2,
          '.',
          ',',
          ''
        );
      }
      if (filteredSelectedJob?.isLenderUnknown && filteredSelectedJob?.lender === '') {
        filteredSelectedJob.lender = 'Lender unknown - unable to identify after due diligence';
      } else if (filteredSelectedJob?.lender === '') {
        filteredSelectedJob.lender = 'N/A';
      }
      if (filteredSelectedJob?.isGCUnknown && filteredSelectedJob?.GC === '') {
        filteredSelectedJob.GC = 'GC unknown - unable to identify after due diligence';
      }
      if (filteredSelectedJob?.isOwnerUnknown && filteredSelectedJob?.owner === '') {
        filteredSelectedJob.owner = 'Owner unknown - unable to identify after due diligence';
      }

      if (
        filteredSelectedJob?.isSurityUnknown &&
        filteredSelectedJob?.isJobBonded &&
        filteredSelectedJob?.surety === ''
      ) {
        filteredSelectedJob.surety = 'Surety unknown - unable to identify after due diligence';
      }
      if (filteredSelectedJob?.bondNumber && filteredSelectedJob?.bondNumber !== '') {
        filteredSelectedJob.bondNumber = `Bond Number: ${filteredSelectedJob.bondNumber}`;
      }
      specialFieldsPDFData.push({
        fromFieldType: 'SUPPLIER_JOB_FIELD',
        dataToUse: filteredSelectedJob,
      });
    }

    if (selectedTemplate) {
      const allNewFormFields = { ...formFieldsData };
      Object.keys(allNewFormFields).forEach((formFieldKey) => {
        if (
          allNewFormFields[formFieldKey].id !== 'signedDate' &&
          !allNewFormFields[formFieldKey].id.includes('MailedDate')
        ) {
          dynamicForFieldsPdfData.push({
            fromFieldId: formFieldKey,
            value: allNewFormFields[formFieldKey].value,
            ...(allNewFormFields[formFieldKey].fontSize
              ? { fontSize: allNewFormFields[formFieldKey].fontSize }
              : {}),
          });
        }
      });
    }

    if (user) {
      specialFieldsPDFData.push({
        fromFieldType: 'USER_SUPPLIER_FIELD',
        dataToUse: user,
      });
    }

    let pdfData: PDFFieldFiledLienTemplate[] = [];
    if (specialFieldsPDFData.length > 0 && selectedTemplate) {
      const data1 = structureTemplateNonNewFieldFromTypePDFData({
        template: selectedTemplate,
        details: specialFieldsPDFData,
      });
      pdfData = [...pdfData, ...data1];
    }
    if (dynamicForFieldsPdfData.length > 0) {
      const data2 = structureTemplateNewFieldFromTypePDFData({
        template: selectedTemplate,
        details: dynamicForFieldsPdfData,
      });
      pdfData = [...pdfData, ...data2];
    }
    if (pdfData.length > 0) {
      populatePdf(pdfData);
    }
  };

  useEffect(() => {
    populateAllPdfData();
  }, [debouncedPopulatePdfChange]);

  useEffect(() => {
    triggerDebouncedPopulatePdf();
    return () => {};
  }, [selectedTemplate, selectedJob, formFieldsData, signature]);

  useEffect(() => {
    if (selectedTemplate) {
      if (selectedTemplateFields) {
        let updateFieldData = {};
        const foundField = [...selectedTemplateFields].find((field) => field.id === 'checkByMail');
        if (foundField) {
          updateFieldData = {
            ...updateFieldData,
            checkByMail: {
              isRequired: foundField.isFieldRequired,
              id: foundField.id,
              name: foundField.fieldName,
              type: foundField.fieldType,
              value: 'X',
            },
          };
        }
        const foundLenders = [...selectedTemplateFields].find((field) => field.id === 'lenders');
        if (foundLenders) {
          const lenders =
            selectedJob?.lenders
              ?.filter((lender) => lender.name !== '')
              ?.map(
                (lender) =>
                  `${lender.name} - ${lender.address}, ${lender.city}, ${lender.state} ${lender.zipCode}`
              ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            lenders: {
              isRequired: foundLenders.isFieldRequired,
              id: foundLenders.id,
              name: foundLenders.fieldName,
              type: foundLenders.fieldType,
              value:
                selectedJob?.isLenderUnknown && lenders.length === 0
                  ? 'Lender unknown - unable to identify after due diligence'
                  : lenders.length === 0
                  ? 'N/A'
                  : lenders.join('\n'),
              fontSize: 10,
            },
          };
        }

        const foundContractors = [...selectedTemplateFields].find(
          (field) => field.id === 'contractors'
        );
        if (foundContractors) {
          const contractors =
            selectedJob?.GCs?.filter((contractor) => contractor.name !== '')?.map(
              (contractor) =>
                `${contractor.name} - ${contractor.address}, ${contractor.city}, ${contractor.state} ${contractor.zipCode}`
            ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            contractors: {
              isRequired: foundContractors.isFieldRequired,
              id: foundContractors.id,
              name: foundContractors.fieldName,
              type: foundContractors.fieldType,
              value:
                selectedJob?.isGCUnknown && contractors.length === 0
                  ? 'GC unknown - unable to identify after due diligence'
                  : contractors.join('\n'),
              fontSize: 10,
            },
          };
        }

        const foundOwners = [...selectedTemplateFields].find((field) => field.id === 'owners');
        if (foundOwners) {
          const owners =
            selectedJob?.owners
              ?.filter((owner) => owner.name !== '')
              ?.map(
                (owner) =>
                  `${owner.name} - ${owner.address}, ${owner.city}, ${owner.state} ${owner.zipCode}`
              ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            owners: {
              isRequired: foundOwners.isFieldRequired,
              id: foundOwners.id,
              name: foundOwners.fieldName,
              type: foundOwners.fieldType,
              value:
                selectedJob?.isOwnerUnknown && owners.length === 0
                  ? 'Owner unknown - unable to identify after due diligence'
                  : owners.join('\n'),
              fontSize: 10,
            },
          };
        }

        const foundSureties = [...selectedTemplateFields].find((field) => field.id === 'sureties');
        if (foundSureties) {
          const sureties =
            selectedJob?.sureties
              ?.filter((surety) => surety.name !== '')
              ?.map(
                (surety) =>
                  `${surety.name} - ${surety.address}, ${surety.city}, ${surety.state} ${
                    surety.zipCode
                  }, Bond Number: ${surety?.bondNumber ?? ''}`
              ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            sureties: {
              isRequired: foundSureties.isFieldRequired,
              id: foundSureties.id,
              name: foundSureties.fieldName,
              type: foundSureties.fieldType,
              value:
                selectedJob?.isSurityUnknown && sureties.length === 0
                  ? 'Surety unknown - unable to identify after due diligence'
                  : sureties.join('\n'),
              fontSize: 10,
            },
          };
        }
        const foundFiledLienMonthField = [...selectedTemplateFields].find(
          (field) => field.id === 'noticeMonth'
        );
        if (foundFiledLienMonthField) {
          const searchParams = new URLSearchParams(location.search);
          const noticeMonth = searchParams.get('month');
          let deadlineDate = moment().subtract(3, 'months').date(15).toDate();
          if (noticeMonth) {
            const noticeMonthDate = moment(noticeMonth, 'MM/DD/YYYY').toDate();
            deadlineDate = noticeMonthDate;
          }
          updateFieldData = {
            ...updateFieldData,
            noticeMonth: {
              isRequired: foundFiledLienMonthField.isFieldRequired,
              id: foundFiledLienMonthField.id,
              name: foundFiledLienMonthField.fieldName,
              type: foundFiledLienMonthField.fieldType,
              value: deadlineDate,
            },
          };
        }
        setFormFieldsData({
          ...formFieldsData,
          ...updateFieldData,
        });
      }
    }
  }, [selectedTemplate, selectedJob, selectedTemplateFields]);

  useEffect(() => {
    const populatedTemplateFields = populateTemplateFieldData();

    setFormFieldsData({
      ...populatedTemplateFields,
    });

    return () => {};
  }, [flattenedPdfUrl]);

  const setupOriginalAndFlattenPDF = async () => {
    if (selectedTemplate) {
      const flat = await removePDFFields(selectedTemplate?.originalPdf?.url ?? '');
      if (flat) {
        setCurrentPdfUrl(flat);
        setFlattenedPdfUrl(flat);
      } else {
        setCurrentPdfUrl(selectedTemplate?.originalPdf?.url ?? '');
        setFlattenedPdfUrl(selectedTemplate?.originalPdf?.url ?? '');
      }
    }
  };

  useEffect(() => {
    if (selectedTemplate) {
      setupOriginalAndFlattenPDF();
      setSelectedTemplateFields(
        [...selectedTemplate.formFields]
          .sort((A, B) => {
            if (
              (A.position === null || A.position === undefined) &&
              B.position !== null &&
              B.position !== undefined
            ) {
              return 1;
            }
            if (
              (B.position === null || B.position === undefined) &&
              A.position !== null &&
              A.position !== undefined
            ) {
              return -1;
            }
            if (
              A.position !== null &&
              A.position !== undefined &&
              B.position !== null &&
              B.position !== undefined &&
              A.position > B?.position
            ) {
              return 1;
            }
            return -1;
          })
          .filter(
            (formFields) =>
              formFields.fromFieldType === 'NEW_FIELD' &&
              formFields.whoPopulatesField === 'SUPPLIER'
          )
      );
    }

    return () => {};
  }, [selectedTemplate]);

  const checkErrors = () => {
    let fields: string[] = [];

    if (!selectedJob) {
      fields = [...fields, 'job'];
    }
    if (!selectedTemplate) {
      fields = [...fields, 'template'];
    }

    Object.keys(formFieldsData).forEach((fieldKey) => {
      const fieldData = formFieldsData[fieldKey];
      if (
        fieldData.isRequired &&
        !fieldData.value &&
        !fieldData.id.startsWith('check') &&
        !fieldData.id.startsWith('signedCity') &&
        !fieldData.id.startsWith('signedDate') &&
        fieldData.id !== 'lenders' &&
        fieldData.id !== 'contractors' &&
        fieldData.id !== 'owners' &&
        fieldData.id !== 'sureties' &&
        !fieldData.id.includes('MailedDate')
      ) {
        fields = [...fields, fieldKey];
      }
    });
    setErrorFields(fields);
    return fields;
  };

  const fetchJobInvoice = async (foundJob: SupplierJobApiResponse) => {
    setSelectedJob(foundJob);
    if (foundJob.jobState.toLowerCase() === 'tx') {
      const result = await dispatch(
        getSupplierJob({
          supplierId: currentSupplier?.id || '',
          supplierJobId: foundJob.id,
        })
      );
      if (result.type === 'supplierJobs/getSupplierJob/fulfilled') {
        const payload = result.payload as any;
        if (payload?.data) {
          const job: SupplierJobApiResponse = payload.data;
          setSelectedJob(job);
        }
      }
    }
  };

  useEffect(() => {
    if (missingJobDetails) {
      const checkMissingDetails = checkMissingJobDetails(selectedJob);
      setMissingJobDetails(checkMissingDetails);
    } else {
      setMissingJobDetails(false);
    }
    return () => {};
  }, [supplierJobsNoInvoicesMap]);

  useEffect(() => {
    const pathSplit = stripSupplierPrefix(location.pathname).split('/');
    if (pathSplit.length === 4 && pathSplit[3] === 'addFiledLien') {
      const searchParams = new URLSearchParams(location.search);
      const jobId = searchParams.get('jobId');

      if (jobId) {
        const foundJob = supplierJobsNoInvoicesMap[jobId];
        if (foundJob && !selectedJob) {
          fetchJobInvoice(foundJob);
        }
      }
    } else if (selectedJob && Object.keys(supplierJobsNoInvoicesMap).length > 0) {
      const updatedSupplierJob = supplierJobsNoInvoicesMap[selectedJob.id];
      if (updatedSupplierJob) {
        setSelectedJob(updatedSupplierJob);
      }
    }
    return () => {};
  }, [location.pathname, supplierJobsNoInvoicesMap]);

  const handleCreateSignature = async () => {
    if (signature.startsWith('data:image')) {
      const foundSignature = userSignatures.find((item) => item.signature === signature);
      if (!foundSignature) {
        if (userSignatures.length > 4) {
          await dispatch(
            deleteUserSignature({ userSignatureId: userSignatures[userSignatures.length - 1].id })
          );
        }
        await dispatch(
          createUserSignature({
            signature,
            date: new Date(),
          })
        );
      }
    }
  };

  const vendorContactTitleId = useMemo(() => {
    if (selectedTemplate) {
      const hasTitle = [...selectedTemplate.formFields].filter(
        (formFields) =>
          formFields.fromFieldType === 'NEW_FIELD' && formFields.id.startsWith('vendorContactTitle')
      );
      if (hasTitle.length > 0) {
        const titleId = hasTitle?.[0]?.id;
        setFormFieldsData({
          ...formFieldsData,
          [titleId]: {
            ...formFieldsData[titleId],
            value: user?.title ?? '',
          },
        });
        return titleId;
      }
      return '';
    }

    return '';
  }, [selectedTemplate]);

  const handleCreateFiledLien = async () => {
    setLoading(true);
    let additionalData = {};
    if (selectedJob?.isJobBonded) {
      additionalData = {
        ...additionalData,
        suretiesStatus:
          selectedJob?.sureties?.map((surety) => ({
            name: surety.name,
            trackLink: '',
          })) ?? [],
      };
    }
    const createdFiledLien = await dispatch(
      createFiledLien({
        supplierId: currentSupplier?.id || '',
        filedLienDetails: {
          supplierId: user?.linkedSupplierIds?.[0] ?? '',
          jobId: selectedJob?.id ?? '',
          status: 'pending',
          fieldsData: Object.values(formFieldsData).filter(
            (ffield) =>
              !ffield.id.startsWith('signedDate') &&
              !ffield.id.startsWith('signedCity') &&
              !ffield.id.includes('MailedDate')
          ),
          templateId: selectedTemplate?.id ?? '',
          ownersStatus:
            selectedJob?.owners?.map((owner) => ({
              name: owner.name,
              trackLink: '',
            })) ?? [],
          contractorsStatus:
            selectedJob?.GCs?.map((contractor) => ({
              name: contractor.name,
              trackLink: '',
            })) ?? [],
          lendersStatus:
            selectedJob?.lenders?.map((lender) => ({
              name: lender.name,
              trackLink: '',
            })) ?? [],
          pdf: {
            url: currentPdfUrl,
            name: selectedTemplate?.name ?? '',
          },
          ...additionalData,
        },
      })
    );
    setLoading(false);
    if (createdFiledLien.type === 'filedLiens/createFiledLien/fulfilled') {
      if (
        user &&
        vendorContactTitleId &&
        formFieldsData[vendorContactTitleId]?.value !== user?.title
      ) {
        const updateUserDetailsResponse = await dispatch(
          updateUserDetails({
            userId: user.id,
            userDetails: { title: formFieldsData[vendorContactTitleId]?.value },
          })
        );
        if (updateUserDetailsResponse.type === 'auth/updateUserDetails/fulfilled') {
          await dispatch(getUserDetails());
        }
      }
      setIsFiledLienCreateSuccessModalOpen(true);
    } else {
      dispatch(
        openMessageModal({
          title: 'Error creating lien!',
          subTitle: `Contact customer support.`,
          buttonText: 'Close',
          modalType: 'ERROR',
          isSecondButton: false,
          onClose: () => {},
        })
      );
    }
  };

  const handleSubmitFiledLien = async () => {
    dispatch(
      openMessageModal({
        title: 'Are you sure you want to file this lien?',
        subTitle: `This action will send this lien to the Dill team for filing. You will be able to find tracking links and additional documentation for this lien filing in your Dill dashboard soon.`,
        buttonText: 'Close',
        modalType: 'WARN',
        isSecondButton: true,
        secondButtonText: 'Yes, Send',
        onSecondButtonClick: () => {
          handleCreateFiledLien();
        },
        onClose: () => {},
      })
    );
  };

  const hasSignature = useMemo(() => {
    if (selectedTemplate) {
      const filteredSignatureField = [...selectedTemplate.formFields].filter(
        (formFields) =>
          formFields.fromFieldType === 'SIGNATURE_FIELD' && formFields.id.startsWith('signature')
      );
      return filteredSignatureField.length > 0;
    }

    return false;
  }, [selectedTemplate]);

  const filteredInvoices = useMemo(() => {
    const noticeMonth = formFieldsData?.noticeMonth?.value;
    if (!selectedJob?.invoices || !noticeMonth) return [];

    const startMonth = moment(noticeMonth).startOf('month');
    const endMonth = moment(noticeMonth).endOf('month');

    return selectedJob.invoices.filter((invoice) => {
      const invoiceDate = invoice?.shipDate ?? invoice?.createdAt;
      return (
        invoice.status !== 'PAID' &&
        invoiceDate &&
        moment(invoiceDate).isAfter(startMonth) &&
        moment(invoiceDate).isBefore(endMonth)
      );
    });
  }, [selectedJob?.invoices, formFieldsData?.noticeMonth?.value]);

  const subUsersInvoices = useMemo(() => {
    return {
      paidInvoices: [],
      unPaidInvoices: filteredInvoices,
    };
  }, [filteredInvoices]);

  const selectedInvoices = filteredInvoices;

  useEffect(() => {
    const claimAmountData = formFieldsData?.claimAmount;
    if (!claimAmountData || selectedInvoices.length === 0) return;

    const totalAmountInvoices = selectedInvoices.reduce((acc, invoice) => {
      return acc + (invoice.amount || 0);
    }, 0);

    setFormFieldsData((prev) => ({
      ...prev,
      claimAmount: {
        ...prev.claimAmount,
        value: claimAmountData.value || Number(Number(totalAmountInvoices)).toFixed(2),
      },
    }));
  }, [selectedInvoices]);

  const handleAddSignature = (newSignature: string, isManual: boolean) => {
    setManualSign(isManual);
    if (isManual) {
      setCurrentPdfUrl(newSignature);
    } else {
      setSignature(newSignature);
    }
    setIsCreateSignatureModalOpen(false);
  };

  const handleDownloadRealease = async () => {
    const createdAtTimeStamp = new Date().getTime();
    const flattened = await flattenPDF(currentPdfUrl || '');
    if (flattened) {
      downloadFile(
        flattened,
        `${selectedJob?.name ? `${selectedJob.name}-` : ''}${
          selectedJob?.buyer?.name ?? ''
        }-${createdAtTimeStamp}.pdf`
      );
    }
  };

  // const checkSignatureError = useMemo(() => {
  //   let fields: string[] = [];
  //   if (selectedTemplate) {
  //     selectedTemplate.formFields.forEach((formField) => {
  //       if (
  //         formField.fromFieldType === 'SIGNATURE_FIELD' &&
  //         formField.whoPopulatesField === 'SUPPLIER'
  //       ) {
  //         if (signature === '' && !manualSign) {
  //           fields = [...fields, formField.id];
  //         }
  //       }
  //     });
  //   }
  //   return fields;
  // }, [formFieldsData, selectedJob, selectedTemplate, signature, manualSign]);

  const checkNonSignatureErrors = useMemo(() => {
    let fields: string[] = [];

    if (!selectedJob) {
      fields = [...fields, 'job'];
    }

    Object.keys(formFieldsData).forEach((fieldKey) => {
      const fieldData = formFieldsData[fieldKey];
      if (
        fieldData.isRequired &&
        !fieldData.value &&
        !fieldData.id.startsWith('check') &&
        !fieldData.id.startsWith('signedCity') &&
        !fieldData.id.startsWith('signedDate') &&
        fieldData.id !== 'lenders' &&
        fieldData.id !== 'contractors' &&
        fieldData.id !== 'owners' &&
        fieldData.id !== 'sureties' &&
        !fieldData.id.includes('MailedDate')
      ) {
        fields = [...fields, fieldKey];
      }
    });
    return fields;
  }, [formFieldsData, selectedJob, selectedTemplate, signature, manualSign]);

  const handleEditJob = (e: React.MouseEvent<HTMLButtonElement>, job: SupplierJobApiResponse) => {
    e.stopPropagation();
    const foundSupplierJob = requestedSupplierJobs.find(
      (requestedSupplierJob) => requestedSupplierJob.supplierJobId === job?.id
    );
    if (foundSupplierJob) {
      dispatch(
        openMessageModal({
          buttonText: 'View Submitted Job Info Sheet',
          modalType: 'INFO',
          title: 'Check Submitted Job Info Sheet!',
          subTitle: `You have a job info sheet submission to review for this job. Before editing this job, please check your Jobs dashboard and review.`,
          onClose: () => {
            setOpenRequestedJobsModal(true);
          },
        })
      );
      return;
    }
    setEditSupplierJob(job);
    setSelectedJob(job);
    setOpenEditJob(true);
  };

  const handlePreviewAttachment = (url: string, fileType: string) => {
    if (fileType && fileType !== 'pdf') {
      setAttachmentUrl(url);
      setShowPreviewImage(true);
    } else {
      setShowPreviewPdf(true);
      setSelectedAttachmentUrl(url);
    }
  };

  const handleDownloadAttachment = (url: string, name: string) => {
    downloadFile(url, name);
  };

  return (
    <div className="flex flex-row h-full py-4">
      <AppRequestedJobsModal
        open={openRequestedJobsModal}
        handleClose={() => {
          setOpenRequestedJobsModal(false);
        }}
        requestedSupplierJobs={requestedSupplierJobs}
        onEditSupplierJob={(data) => {
          setEditSupplierJob(data);
          setOpenEditJob(true);
        }}
        onReload={() => {}}
        onPreviewAttachment={handlePreviewAttachment}
        onDownloadAttachment={handleDownloadAttachment}
      />
      <AppImagePreviewModal
        open={showPreviewImage}
        handleClose={() => {
          setShowPreviewImage(false);
          setAttachmentUrl('');
        }}
        title="Job Info Sheet Attachment"
        url={attachmentUrl}
      />
      <AppPdfPreviewModal
        open={showPreviewPdf}
        handleClose={() => {
          setShowPreviewPdf(false);
        }}
        invoice={''}
        pdfType={'PRELIMINARY_NOTICE'}
        pdfList={[
          {
            name: 'attachment',
            url: selectedAttachmentUrl,
            date: new Date(),
          },
        ]}
        handleDownloadPdf={() => {}}
        isSigned={false}
        showPendingMessage={false}
        mode="Supplier"
        showHistoryButton={false}
      />
      <AppNewSupplierJobModal
        open={openEditJob}
        editMode={true}
        selectedSupplierJob={editSupplierJob}
        handleClose={({ wasBuyerEdited }) => {
          setOpenEditJob(false);
          if (wasBuyerEdited && currentSupplier?.id) {
            recursiveFetchSupplierJob({
              lastDockId: '',
              fetchJobs: [],
              supplierId: currentSupplier.id,
            });
          }
        }}
      />
      <CreateSignatureModal
        open={isCreateSignatureModalOpen}
        handleClose={() => {
          setIsCreateSignatureModalOpen(false);
        }}
        addSignature={handleAddSignature}
        onDownloadRealease={handleDownloadRealease}
        defaultIsManualSign={defaultIsManualSign}
      />
      <AppSuccessModal
        open={isFiledLienCreateSuccessModalOpen}
        modalImage={successChecksCircle}
        title="Successfully submitted lien"
        subTitle="Your lien has been submitted"
        buttonText="View All Liens"
        handleClose={() => {
          setIsFiledLienCreateSuccessModalOpen(false);
          navigate(buildSupplierPath(currentSupplier?.id, '/liens/overview'));
        }}
      />
      <div className={twMerge('flex flex-col w-5/12 mx-4 h-5/6 overflow-scroll hidescrollbar')}>
        <div className={twMerge('flex flex-col  bg-white px-4 py-3 mb-3')}>
          <div className="my-2 flex ">
            <AppDropDown2
              label="Job"
              isRequired={true}
              errorText={
                errorFields.includes('job')
                  ? 'Please select a job'
                  : `${
                      missingJobDetails
                        ? `Job is missing key information like ${missingJobDetailsList.join(
                            ', '
                          )}. Please update the job in the dropdown above.`
                        : ''
                    }`
              }
              value={selectedJob?.id || ''}
              isLoading={!dataFullyLoaded}
              items={[
                ...Object.values(supplierJobsNoInvoicesMap)
                  .filter((jb) => !jb?.isArchived)
                  .map((job) => {
                    return {
                      label: `${job?.jobNumber ? `${job.jobNumber} - ` : ''}${job.name}`,
                      value: job.id,
                      labelInElement: (
                        <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                          <div className="text-xs">{`${
                            job?.jobNumber ? `${job.jobNumber} - ` : ''
                          }${job.name}`}</div>
                          <button
                            onClick={(e) => {
                              handleEditJob(e, job);
                            }}
                            className="text-xs px-4 py-1 WHITE-BG font-bold TEXT_SECONDARY-CLR rounded absolute hidden group-hover:inline-block right-2">
                            Edit
                          </button>
                        </div>
                      ),
                    };
                  }),
              ]}
              onSelectChange={async (item) => {
                if (item?.value) {
                  const uu = supplierJobsNoInvoicesMap[item.value];
                  if (uu) {
                    setSelectedJob(uu);
                    if (uu.jobState.toLowerCase() === 'tx') {
                      const result = await dispatch(
                        getSupplierJob({
                          supplierId: currentSupplier?.id || '',
                          supplierJobId: uu.id,
                        })
                      );
                      if (result.type === 'supplierJobs/getSupplierJob/fulfilled') {
                        const payload = result.payload as any;
                        if (payload?.data) {
                          const job: SupplierJobApiResponse = payload.data;
                          setSelectedJob(job);
                        }
                      }
                    }
                    setEditSupplierJob(uu);
                    setMissingJobDetails(false);
                    setShowErrorAbove(false);
                  }
                }
              }}
              disabled={manualSign}
            />
          </div>
          <div className="my-1">
            <AppDropDown
              label="Lien Template"
              isRequired={true}
              errorText={errorFields.includes('template') ? 'Please select template' : ''}
              value={selectedTemplate ? selectedTemplate.id : ''}
              items={[
                ...filteredTemplates.map((template) => {
                  return {
                    label: template.name,
                    value: template.id,
                  };
                }),
              ]}
              onSelectChange={(item) => {
                if (item?.value) {
                  const uu = filteredTemplates.find((val) => val.id === item.value);
                  if (uu) {
                    setSelectedTemplate(uu);
                    setShowErrorAbove(false);
                  }
                }
              }}
              isDisabled={manualSign}
            />
          </div>

          {selectedTemplate && (
            <div className="flex flex-wrap ">
              {selectedTemplateFields &&
                selectedTemplateFields.length > 0 &&
                [...selectedTemplateFields]
                  .filter(
                    (ffield) =>
                      !ffield.id.startsWith('check') &&
                      !ffield.id.startsWith('signedDate') &&
                      !ffield.id.startsWith('signedCity') &&
                      ffield.id !== 'lenders' &&
                      ffield.id !== 'contractors' &&
                      ffield.id !== 'owners' &&
                      ffield.id !== 'sureties' &&
                      !ffield.id.includes('MailedDate')
                  )
                  .map((formField, i) => {
                    const {
                      id,
                      fieldType,
                      fieldName,
                      isFieldRequired,
                      suffix,
                      prefix,
                      maxValue,
                      minValue,
                    } = formField;
                    const fieldValue = formFieldsData[id]?.value || '';
                    let finalFieldName = fieldName;
                    if (id === vendorContactTitleId) {
                      finalFieldName = 'Job Title';
                    }
                    return (
                      <div
                        key={i + 'oo'}
                        className={twMerge(
                          'w-1/2 my-2',
                          i % 2 === 0 ? 'pr-1' : 'pl-1',
                          i ===
                            selectedTemplate.formFields.filter(
                              (formFields) =>
                                formFields.fromFieldType === 'NEW_FIELD' &&
                                !formFields.id.startsWith('signedDate') &&
                                formFields.id !== 'lenders' &&
                                formFields.id !== 'contractors' &&
                                formFields.id !== 'owners' &&
                                formFields.id !== 'sureties' &&
                                !formField.id.includes('MailedDate') &&
                                formFields.whoPopulatesField === 'SUPPLIER'
                            ).length -
                              1 && i % 2 === 0
                            ? 'w-full p-0'
                            : '',
                          fieldType === 'TEXT_AREA' ? 'w-full p-0' : ''
                        )}>
                        {(fieldType === 'TEXT' ||
                          fieldType === 'NUMBER' ||
                          fieldType === 'CURRENCY' ||
                          fieldType === 'TEXT_AREA') && (
                          <AppInputField
                            label={finalFieldName}
                            inputType={
                              fieldType === 'TEXT_AREA'
                                ? 'textarea'
                                : fieldType === 'NUMBER' || fieldType === 'CURRENCY'
                                ? 'number'
                                : 'text'
                            }
                            value={
                              fieldValue !== null && fieldValue !== undefined ? fieldValue : ''
                            }
                            isRequired={isFieldRequired}
                            errorText={errorFields.includes(id) ? 'Required' : ''}
                            suffix={suffix || ''}
                            prefix={prefix || ''}
                            onBlur={(e) => {
                              let val = e.target.value;
                              if (minValue && parseInt(val) <= minValue) {
                                setFormFieldsData({
                                  ...formFieldsData,
                                  [id]: { ...formFieldsData[id], value: minValue },
                                });
                                return;
                              }
                              if (maxValue && parseInt(val) >= maxValue) {
                                setFormFieldsData({
                                  ...formFieldsData,
                                  [id]: { ...formFieldsData[id], value: maxValue },
                                });
                                return;
                              }
                              if (fieldType === 'CURRENCY') {
                                if (!val) {
                                  val = null;
                                } else {
                                  val = Number(Number(val)).toFixed(2);
                                }
                                setFormFieldsData({
                                  ...formFieldsData,
                                  [id]: { ...formFieldsData[id], value: val },
                                });
                              }
                            }}
                            onTextChange={(text) => {
                              let val = text;

                              if (fieldType === 'NUMBER') {
                                if (text !== undefined && text !== null) {
                                  val = text;
                                } else {
                                  val = null;
                                }
                              } else if (fieldType === 'CURRENCY') {
                                if (text !== undefined && text !== null) {
                                  val = text;
                                } else {
                                  val = null;
                                }
                              }
                              setShowErrorAbove(false);
                              setFormFieldsData({
                                ...formFieldsData,
                                [id]: { ...formFieldsData[id], value: val },
                              });
                            }}
                            isDisabled={manualSign}
                          />
                        )}
                        {fieldType === 'DATE' && (
                          <AppDatePicker
                            label={finalFieldName}
                            selectedDate={fieldValue}
                            isRequired={isFieldRequired}
                            errorText={errorFields.includes(id) ? 'Required' : ''}
                            onDateSelected={(date) => {
                              setShowErrorAbove(false);
                              let selectedDate = date;
                              if (id === 'noticeMonth' && date) {
                                selectedDate = moment(date).date(15).toDate();
                              }
                              setFormFieldsData({
                                ...formFieldsData,
                                [id]: { ...formFieldsData[id], value: selectedDate },
                              });
                            }}
                            showMonthYearPicker={id === 'noticeMonth'}
                            isDisabled={manualSign}
                          />
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
          {selectedTemplate && (
            <div className="flex flex-wrap ">
              {selectedTemplateFields &&
                selectedTemplateFields.length > 0 &&
                [...selectedTemplateFields]
                  .filter(
                    (ffield) =>
                      !ffield.id.startsWith('checkByMail') &&
                      !ffield.id.startsWith('signedDate') &&
                      ffield.id !== 'lenders' &&
                      ffield.id !== 'contractors' &&
                      ffield.id !== 'owners' &&
                      ffield.id !== 'sureties' &&
                      !ffield.id.includes('MailedDate')
                  )
                  .map((formField, i) => {
                    const {
                      id,
                      fieldType,
                      fieldName,
                      isFieldRequired,
                      suffix,
                      prefix,
                      maxValue,
                      minValue,
                    } = formField;
                    const fieldValue = formFieldsData[id]?.value || '';
                    return (
                      <div key={i + 'oo'} className={twMerge('w-full')}>
                        {id.startsWith('check') && (
                          <div className="my-1 flex items-center mb-2">
                            <AppCheckBox
                              isChecked={
                                fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
                                  ? true
                                  : false
                              }
                              onClick={() => {
                                if (
                                  fieldValue !== null &&
                                  fieldValue !== undefined &&
                                  fieldValue !== ''
                                ) {
                                  setFormFieldsData({
                                    ...formFieldsData,
                                    [id]: { ...formFieldsData[id], value: null },
                                  });
                                } else {
                                  setFormFieldsData({
                                    ...formFieldsData,
                                    [id]: { ...formFieldsData[id], value: 'X' },
                                  });
                                }
                              }}
                              isDisabled={manualSign}
                            />
                            <div className="ml-2 text-sm">{fieldName}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
          {selectedTemplate && (
            <div className="flex flex-wrap ">
              {selectedTemplateFields &&
                selectedTemplateFields.length > 0 &&
                [...selectedTemplateFields]
                  .filter((ffield) => ffield.id.startsWith('checkByMail'))
                  .map((formField, i) => {
                    const {
                      id,
                      fieldType,
                      fieldName,
                      isFieldRequired,
                      suffix,
                      prefix,
                      maxValue,
                      minValue,
                    } = formField;
                    const fieldValue = formFieldsData[id]?.value || '';
                    return (
                      <div key={i + 'oo'} className={twMerge('w-full')}>
                        {id.startsWith('check') && (
                          <div className="my-1 flex items-center mb-2">
                            <AppCheckBox
                              isChecked={
                                fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
                                  ? true
                                  : false
                              }
                              onClick={() => {
                                if (
                                  fieldValue !== null &&
                                  fieldValue !== undefined &&
                                  fieldValue !== ''
                                ) {
                                  setFormFieldsData({
                                    ...formFieldsData,
                                    [id]: { ...formFieldsData[id], value: null },
                                  });
                                } else {
                                  setFormFieldsData({
                                    ...formFieldsData,
                                    [id]: { ...formFieldsData[id], value: 'X' },
                                  });
                                }
                              }}
                              isDisabled={true}
                            />
                            <div className="ml-2 text-sm">{fieldName}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
          <div>
            {/* {hasSignature && (
              <div className="flex">
                <AppButton
                  text={signature !== '' ? 'Signed' : 'Sign'}
                  type="PRIMARY"
                  buttonStyles={{ width: '100px', height: '35px' }}
                  onClick={() => {
                    setDefaultIsManualSign(false);
                    setIsCreateSignatureModalOpen(true);
                  }}
                  isDisabled={manualSign || signature !== ''}
                />
                <AppButton
                  text="Download & Manual Upload"
                  type="SECONDARY"
                  buttonStyles={{
                    marginLeft: '4px',
                    height: '35px',
                  }}
                  onClick={() => {
                    const checkMissingDetails = checkMissingJobDetails(selectedJob);
                    setMissingJobDetails(checkMissingDetails);
                    if (!checkMissingDetails) {
                      setDefaultIsManualSign(true);
                      setIsCreateSignatureModalOpen(true);
                    }
                  }}
                  isDisabled={
                    checkNonSignatureErrors.length !== 0 || manualSign || signature !== ''
                  }
                />
              </div>
            )} */}
          </div>
          {filteredTemplates.length === 0 && selectedJob && selectedJob.jobState !== '' && (
            <div className="flex flex-row items-center mt-2 gap-2 p-1 PRIMARY_100-BG rounded-md">
              <div>
                <Note color={COLORS.PRIMARY_500} size={20} />
              </div>
              <div className="text-xs">{`This job is located in state ${selectedJob.jobState}. Your account is not setup to file liens for jobs in state ${selectedJob.jobState}. Please reach out to customer support.`}</div>
            </div>
          )}
          {selectedJob &&
            selectedJob?.jobState.toLowerCase() === 'tx' &&
            subUsersInvoices.unPaidInvoices.length > 0 && (
              <div>
                <div className="py-1 border-b GREY_200-BORDER font-bold text-xs">Invoices</div>
                <div className="w-full overflow-x-scroll pt-1">
                  <AppInvoiceTable
                    invoices={subUsersInvoices}
                    onSelectInvoice={() => {}}
                    invoicesSelected={selectedInvoices}
                    tableStyle={{
                      maxHeight: `calc(100vh - 400px)`,
                    }}
                  />
                </div>
              </div>
            )}
        </div>
        {showErrorAbove && (
          <div className="my-2 text-xs ERROR_500-CLR text-center">{`${
            checkNonSignatureErrors.length > 0 || missingJobDetails
              ? `Please see errors above `
              : ''
          }`}</div>
        )}
        <div className="flex flex-row justify-end ">
          <AppButton
            text="Close"
            type="SECONDARY"
            buttonStyles={{ width: '100px', height: '40px' }}
            onClick={() => {
              navigate(buildSupplierPath(currentSupplier?.id, '/liens/overview'));
            }}
          />
          <div className="flex flex-row align-center ml-3">
            <AppButton
              text="Submit"
              buttonStyles={{ width: '100px', height: '40px' }}
              onClick={() => {
                const fieldsWithErrors = checkErrors();
                const checkMissingDetails = checkMissingJobDetails(selectedJob);
                setShowErrorAbove(fieldsWithErrors.length !== 0 || checkMissingDetails);
                setMissingJobDetails(checkMissingDetails);
                if (fieldsWithErrors.length === 0 && !checkMissingDetails) {
                  handleSubmitFiledLien();
                }
              }}
              isLoading={loading}
              isDisabled={loading}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col w-7/12 h-full">
        <div className="h-5/6 relative  overflow-scroll flex flex-col items-center pb-3">
          <div
            style={{
              width: '612px',
              background: '#F4F7FA',
            }}
            className="sticky top-0  z-10 flex justify-between  mb-0.5"></div>

          <div
            style={{
              width: '612px',
              height: '792px',
            }}
            className="relative ">
            {currentPdfUrl && (
              <Viewer
                defaultScale={1}
                fileUrl={currentPdfUrl}
                plugins={[zoomPluginInstance, pageNavigationPluginInstance]}
                initialPage={currentPage}
                onPageChange={(e) => setCurrentPage(e.currentPage)}
                renderLoader={(percentages: number) => (
                  <div style={{ width: '240px' }}>
                    <ProgressBar progress={Math.round(percentages)} />
                  </div>
                )}
              />
            )}
          </div>
        </div>

        <div className="flex flex-row justify-center">
          <div className="mx-4">
            <CurrentPageLabel>
              {(props: RenderCurrentPageLabelProps) => {
                return (
                  <div className="mx-4 flex items-center justify-center">
                    {props.numberOfPages > 1 && (
                      <button
                        className={`mr-2 flex items-center justify-center py-[2px] px-2 ${
                          props.numberOfPages > props.currentPage + 1
                            ? 'GREY_500-CLR cursor-default'
                            : 'hover:bg-blue-100  hover:text-[#3762FB]'
                        }`}
                        onClick={() => {
                          setCurrentPage(props.currentPage - 1);
                          jumpToPreviousPage();
                        }}>
                        <CaretCircleLeft className="mr-1" />
                        Previous Page
                      </button>
                    )}
                    <span>{`${props.numberOfPages > 1 ? '' : 'page'} ${props.currentPage + 1} / ${
                      props.numberOfPages
                    }`}</span>
                    {props.numberOfPages > 1 && (
                      <button
                        className={`ml-2 flex items-center justify-center py-[2px] px-2 ${
                          props.numberOfPages - 1 < props.currentPage + 1
                            ? 'GREY_500-CLR cursor-default'
                            : 'hover:bg-blue-100 hover:text-[#3762FB]'
                        }`}
                        onClick={() => {
                          jumpToNextPage();
                          setCurrentPage(props.currentPage + 1);
                        }}>
                        Next Page
                        <CaretCircleRight className="ml-1" />
                      </button>
                    )}
                  </div>
                );
              }}
            </CurrentPageLabel>
          </div>

          <ZoomOutButton />
          <ZoomPopover />
          <ZoomInButton />
        </div>
      </div>
    </div>
  );
};

export default AddFiledLien;
